import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

/** Renders a single PDF page into a canvas at the given CSS width (DPR-aware for crispness). */
const PdfPageCanvas = ({ pdfDoc, pageNumber, width }) => {
  const canvasRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);

  useEffect(() => {
    if (!pdfDoc || !width) return undefined;
    let cancelled = false;
    let renderTask = null;

    (async () => {
      try {
        setIsRendering(true);
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = width / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const outputScale = window.devicePixelRatio || 1;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        canvas.width = Math.ceil(viewport.width * outputScale);
        canvas.height = Math.ceil(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;
        renderTask = page.render({ canvasContext: context, viewport, transform });
        await renderTask.promise;
        if (!cancelled) setIsRendering(false);
      } catch (error) {
        if (error?.name !== 'RenderingCancelledException') {
          console.error(`Failed to render PDF page ${pageNumber}`, error);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDoc, pageNumber, width]);

  return (
    <div className="pdf-splitter-thumb-canvas-wrapper">
      {isRendering && (
        <div className="pdf-splitter-thumb-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      <canvas ref={canvasRef} className="pdf-splitter-thumb-canvas" />
    </div>
  );
};

export default PdfPageCanvas;
